"use client";

import { useState, type CSSProperties } from "react";
import {
  ArrowRight,
  Check,
  Code2,
  FileText,
  Globe2,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import type { SeoTechnicalCopy } from "./seo-specialist-copy";
import styles from "./seo-specialist.module.scss";

export function CcSeoCrawlDemo({ copy }: { copy: SeoTechnicalCopy["crawl"] }) {
  const [fixed, setFixed] = useState(false);
  const icons = [Globe2, Search, Code2, FileText];
  return (
    <section
      className={`cc-section ${styles.crawlSection}`}
      id="seo-example"
      aria-labelledby="seo-crawl-title"
      data-cc-nav="light"
      data-cc-surface="light"
    >
      <div className={styles.shell}>
        <div className={styles.splitHeader}>
          <div className={styles.sectionHeader}>
            <CcSanjayaEyebrow>{copy.eyebrow}</CcSanjayaEyebrow>
            <h2 id="seo-crawl-title">{copy.title}</h2>
            <p>{copy.text}</p>
          </div>
          <div className={styles.segmented} aria-label={copy.example}>
            <button
              type="button"
              aria-pressed={!fixed}
              onClick={() => setFixed(false)}
            >
              {copy.before}
            </button>
            <button
              type="button"
              aria-pressed={fixed}
              onClick={() => setFixed(true)}
            >
              {copy.after}
            </button>
          </div>
        </div>
        <div className={styles.crawlCanvas} data-fixed={fixed}>
          <small className={styles.canvasLabel}>{copy.example}</small>
          <ol className={styles.crawlPath}>
            {copy.steps.map((step, index) => {
              const Icon = icons[index];
              const blocked = index === 3 && !fixed;
              return (
                <li key={step} data-blocked={blocked}>
                  <div className={styles.crawlNode}>
                    <Icon size={32} strokeWidth={1.4} aria-hidden="true" />
                    <span className={styles.nodeCheck}>
                      {blocked ? <X size={12} /> : <Check size={12} />}
                    </span>
                  </div>
                  <strong>{step}</strong>
                  <small>
                    {index === 3
                      ? fixed
                        ? copy.allowed
                        : copy.blocked
                      : `0${index + 1}`}
                  </small>
                  {index < 3 && (
                    <ArrowRight
                      className={styles.pathArrow}
                      size={22}
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
          <div className={styles.crawlResult} aria-live="polite">
            <span>
              {fixed ? <ShieldCheck size={28} /> : <Code2 size={28} />}
            </span>
            <div>
              <h3>{fixed ? copy.afterTitle : copy.beforeTitle}</h3>
              <p>{fixed ? copy.afterText : copy.beforeText}</p>
            </div>
            <code>{fixed ? 'robots="index, follow"' : 'robots="noindex"'}</code>
          </div>
        </div>
        <p className={styles.exampleNote}>{copy.note}</p>
      </div>
    </section>
  );
}

export function CcSeoVitalsDemo({
  copy,
}: {
  copy: SeoTechnicalCopy["vitals"];
}) {
  const [fixed, setFixed] = useState(false);
  return (
    <section
      className={`cc-section ${styles.vitalsSection}`}
      aria-labelledby="seo-vitals-title"
      data-cc-nav="light"
      data-cc-surface="light"
    >
      <div className={styles.shell}>
        <div className={styles.splitHeader}>
          <div className={styles.sectionHeader}>
            <CcSanjayaEyebrow>{copy.eyebrow}</CcSanjayaEyebrow>
            <h2 id="seo-vitals-title">{copy.title}</h2>
            <p>{copy.text}</p>
          </div>
          <div className={styles.segmented} aria-label={copy.label}>
            <button
              type="button"
              aria-pressed={!fixed}
              onClick={() => setFixed(false)}
            >
              {copy.before}
            </button>
            <button
              type="button"
              aria-pressed={fixed}
              onClick={() => setFixed(true)}
            >
              {copy.after}
            </button>
          </div>
        </div>
        <div className={styles.vitalCards} aria-live="polite">
          {copy.metrics.map((metric) => (
            <article
              className={styles.vitalCard}
              data-good={fixed}
              key={metric.key}
            >
              <div className={styles.vitalCardTop}>
                <span>{metric.key}</span>
                <small>
                  <i />
                  {fixed ? copy.good : copy.poor}
                </small>
              </div>
              <div
                className={styles.gauge}
                style={
                  {
                    "--progress": `${fixed ? metric.afterProgress : metric.beforeProgress}%`,
                  } as CSSProperties
                }
              >
                <div>
                  <strong>{fixed ? metric.after : metric.before}</strong>
                  <span>{metric.target}</span>
                </div>
              </div>
              <h3>{metric.name}</h3>
              <p>{metric.text}</p>
            </article>
          ))}
        </div>
        <p className={styles.exampleNote}>
          {copy.note}{" "}
          <a href={copy.sourceHref} target="_blank" rel="noopener noreferrer">
            {copy.sourceLabel}
            <ArrowRight size={12} aria-hidden="true" />
          </a>
        </p>
      </div>
    </section>
  );
}
